'use client'

import { useEffect, useRef } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getRoot, $isTextNode, $isParagraphNode } from 'lexical'
import { $isMentionNode } from './MentionNode'
import { computeChecksum } from './scriptUtils'

function collectSectionText(paragraphs, startPIndex, startChildIndex) {
    let text = ''
    for (let p = startPIndex; p < paragraphs.length; p++) {
        const children = paragraphs[p].getChildren()

        if (p > startPIndex) {
            if (children.length === 0 ||
                (children.length === 1 && $isTextNode(children[0]) && children[0].getTextContent().trim() === '')) {
                break
            }
            text += '\n'
        }

        const startIdx = (p === startPIndex) ? startChildIndex : 0
        for (let c = startIdx; c < children.length; c++) {
            const child = children[c]
            if ($isMentionNode(child)) {
                const data = child.getSerializedData()
                if (data.type === 'break') {
                    text += `[break:${data.duration || 0}]`
                } else if (data.type === 'emotion') {
                    text += `[emotion:${data.emotion || ''}]`
                }
            } else {
                text += child.getTextContent()
            }
        }
    }
    return text
}

export function serializeEditorState(editorState) {
    let result = ''
    editorState.read(() => {
        const root = $getRoot()
        const paragraphs = root.getChildren().filter($isParagraphNode)

        const mentionPositions = []

        paragraphs.forEach((paragraph, pIndex) => {
            const children = paragraph.getChildren()
            children.forEach((child, cIndex) => {
                if ($isMentionNode(child)) {
                    const mentionType = child.getMentionType()
                    const mentionId = child.getMentionId()
                    if (mentionType === 'character' || mentionId === 'segment' || mentionId === 'scene' || mentionId === 'description') {
                        mentionPositions.push({ pIndex, cIndex, node: child })
                    }
                }
            })
        })

        const checksumMap = new Map()
        for (const { pIndex, cIndex, node } of mentionPositions) {
            const sectionText = collectSectionText(paragraphs, pIndex, cIndex + 1)
            checksumMap.set(node.getKey(), computeChecksum(sectionText))
        }

        paragraphs.forEach((paragraph, pIndex) => {
            if (pIndex > 0) result += '\n'

            const children = paragraph.getChildren()
            children.forEach(child => {
                if ($isMentionNode(child)) {
                    const data = child.getSerializedData()
                    const checksum = checksumMap.get(child.getKey())
                    if (checksum !== undefined) {
                        data.checksum = checksum
                    }
                    result += `{${JSON.stringify(data)}}`
                } else {
                    result += child.getTextContent()
                }
            })
        })
    })
    return result
}

export function parseNotationString(text) {
    const segments = []
    let remaining = text
    const pattern = /\{(\{.*?\})\}/g
    let lastIndex = 0
    let match

    pattern.lastIndex = 0
    while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ type: 'text', value: text.slice(lastIndex, match.index) })
        }

        try {
            const data = JSON.parse(match[1])
            segments.push({ type: 'mention', value: data })
        } catch {
            segments.push({ type: 'text', value: match[0] })
        }
        lastIndex = pattern.lastIndex
    }

    if (lastIndex < text.length) {
        segments.push({ type: 'text', value: text.slice(lastIndex) })
    }

    return segments
}

export default function SerializationPlugin({ onChange }) {
    const [editor] = useLexicalComposerContext()
    const prevRef = useRef('')

    useEffect(() => {
        return editor.registerUpdateListener(({ editorState, dirtyElements, dirtyLeaves }) => {
            if (dirtyElements.size === 0 && dirtyLeaves.size === 0) return

            const serialized = serializeEditorState(editorState)
            if (serialized !== prevRef.current) {
                prevRef.current = serialized
                if (onChange) {
                    onChange(serialized)
                }
            }
        })
    }, [editor, onChange])

    return null
}
